import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { useAccountStore } from '@/stores/accountStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import type { Analysis } from '@/globals/typescript/models/Analysis'
import type {
  ICustomerAccountPriceAnalysisEntry,
  ICustomerAccountPriceTatEntry,
  ICustomerAccountPriceAddonEntry,
  TCustomerAccountPriceDealEntry,
} from '@team-milva/lab-models/customerAccount'
import type { TPricingSegmentDealEntry } from '@/stores/pricingSegmentStore'

/**
 * Seeds customer-level pricing overrides so the four pricing tabs can be tested
 * without hand-building each state.
 *
 * Writes onto **existing** customers rather than creating any: a private
 * customer needs a real Firebase Auth account (there is no Auth emulator here —
 * see `.claude/instructions/local-dev-auth.md`), so creating customers is not
 * something a client-side seed can do.
 *
 * Reads from the Pinia stores rather than querying Firestore, matching
 * `runSeedOrders` — it needs model instances anyway, for TAT compatibility.
 *
 * Every entry is written in the current `defaultAction` shape. See the
 * base/extension contract in the Customer Pricing feature doc.
 */

/** What one customer type's seed produced, for the confirmation toast. */
export interface ISeedCustomerPricingResult {
  segmentName: string
  seeded: { label: string; name: string }[]
  skipped: { label: string; reason: string }[]
}

interface IOverrideSet {
  analyses: ICustomerAccountPriceAnalysisEntry[]
  tats: ICustomerAccountPriceTatEntry[]
  addons: ICustomerAccountPriceAddonEntry[]
  deals: TCustomerAccountPriceDealEntry[]
}

/**
 * Builds one override set covering every state the pricing tabs can render, so
 * each is reachable straight after seeding:
 *
 * **Analyser** — one analysis left inherited (`Samme`, no entry at all), one
 * with the customer's own price, one disabled.
 *
 * **Svartider** — a default (`De resterende`) line with a price, a fully
 * disabled TAT, a scoped line with a price and no default, and the mixed case:
 * a priced default line alongside a scoped line that disables specific
 * analyses.
 *
 * **Tillægsydelser** — every other add-on overridden, the rest inherited.
 *
 * **Tilbud** — the first base deal left alone (`Samme`), the second disabled
 * for this customer, and (when the segment has an un-dealt analysis left) a
 * standalone deal of the customer's own on top of that.
 *
 * Prices are derived from the segment's own so the difference is obvious in the
 * UI (and so the struck-through inherited value is never equal to the override).
 */
function buildOverrideSet({
  segmentAnalyses,
  segmentTats,
  segmentAddons,
  segmentDeals,
  compatibleAnalysisIDsByTatID,
}: {
  segmentAnalyses: { analysisID: string; priceValue: number }[]
  segmentTats: { tatID: string; defaultPrice: number | null }[]
  segmentAddons: { addonID: string; priceValue: number }[]
  segmentDeals: TPricingSegmentDealEntry[]
  compatibleAnalysisIDsByTatID: Map<string, string[]>
}): IOverrideSet {
  const analyses: ICustomerAccountPriceAnalysisEntry[] = []

  // segmentAnalyses[0] is deliberately left alone — no entry means 'Samme',
  // which is the state that must store nothing at all.
  const overridden = segmentAnalyses[1]
  if (overridden) {
    analyses.push({
      analysisID: overridden.analysisID,
      priceValue: Math.round(overridden.priceValue * 0.9),
      entryType: 'enabled',
    })
  }
  const disabled = segmentAnalyses[2]
  if (disabled) {
    analyses.push({
      analysisID: disabled.analysisID,
      priceValue: disabled.priceValue,
      entryType: 'disabled',
    })
  }

  const tats: ICustomerAccountPriceTatEntry[] = []
  const priceFor = (tat: { defaultPrice: number | null }, factor: number) =>
    Math.round((tat.defaultPrice ?? 100) * factor)

  // 1 — default line, priced.
  const tatDefault = segmentTats[0]
  if (tatDefault) {
    tats.push({
      tatID: tatDefault.tatID,
      defaultAction: 'price',
      defaultPrice: priceFor(tatDefault, 0.8),
      entries: [],
    })
  }

  // 2 — the whole TAT off for this customer.
  const tatOff = segmentTats[1]
  if (tatOff) {
    tats.push({
      tatID: tatOff.tatID,
      defaultAction: 'disabled',
      defaultPrice: null,
      entries: [],
    })
  }

  // 3 — a scoped line only, no default. Everything the scoped line does not
  // claim keeps falling through to the segment.
  const tatScoped = segmentTats[2]
  const scopedIDs = tatScoped
    ? (compatibleAnalysisIDsByTatID.get(tatScoped.tatID) ?? []).slice(0, 2)
    : []
  if (tatScoped && scopedIDs.length) {
    tats.push({
      tatID: tatScoped.tatID,
      defaultAction: null,
      defaultPrice: null,
      entries: [
        {
          action: 'price',
          priceValue: priceFor(tatScoped, 1.2),
          applicableAnalysisIDs: scopedIDs,
        },
      ],
    })
  }

  // 4 — the mixed case: a priced default line plus a scoped line that disables
  // specific analyses. This is the one that proves a per-(TAT x analysis)
  // disable is a suppress, not a whole-TAT switch.
  const tatMixed = segmentTats[3]
  const mixedIDs = tatMixed
    ? (compatibleAnalysisIDsByTatID.get(tatMixed.tatID) ?? []).slice(0, 1)
    : []
  if (tatMixed && mixedIDs.length) {
    tats.push({
      tatID: tatMixed.tatID,
      defaultAction: 'price',
      defaultPrice: priceFor(tatMixed, 0.95),
      entries: [
        {
          action: 'disabled',
          priceValue: null,
          applicableAnalysisIDs: mixedIDs,
        },
      ],
    })
  }

  // Every other add-on overridden; the rest stay inherited, so both states are
  // on screen at once as soon as the lab has two or more add-ons.
  const addons: ICustomerAccountPriceAddonEntry[] = segmentAddons
    .filter((_, index) => index % 2 === 0)
    .map((addon) => ({
      addonID: addon.addonID,
      priceValue: Math.round(addon.priceValue * 1.25),
    }))

  const deals: TCustomerAccountPriceDealEntry[] = []

  // deals[0] is deliberately left alone — no entry means 'Samme', matching
  // how the un-overridden analysis above stores nothing either.

  // deals[1] — disabled for this customer. Structurally required to carry a
  // copy of the base deal's own tier/interval config even though it's
  // irrelevant while disabled — see `CustomerPricingEditor.vue`'s
  // `setDealAction()`, which this mirrors.
  const dealToDisable = segmentDeals[1]
  if (dealToDisable) {
    deals.push(
      dealToDisable.dealType === 'tiered-pricing'
        ? {
            analysisID: dealToDisable.analysisID,
            dealType: 'tiered-pricing',
            entryType: 'disabled',
            tier1: dealToDisable.tier1,
            tier2: dealToDisable.tier2,
          }
        : {
            analysisID: dealToDisable.analysisID,
            dealType: 'interval-pricing',
            entryType: 'disabled',
            intervalCount: dealToDisable.intervalCount,
            intervalPrice: dealToDisable.intervalPrice,
          }
    )
  }

  // A standalone deal of the customer's own, on an analysis the segment has
  // no deal for — "one deal per analysis, period" means this only works on
  // an analysis outside `segmentDeals`.
  const dealtAnalysisIDs = new Set(segmentDeals.map((x) => x.analysisID))
  const standaloneCandidate = segmentAnalyses.find(
    (x) => !dealtAnalysisIDs.has(x.analysisID)
  )
  if (standaloneCandidate) {
    deals.push({
      analysisID: standaloneCandidate.analysisID,
      dealType: 'interval-pricing',
      entryType: 'enabled',
      intervalCount: 3,
      intervalPrice: Math.round(standaloneCandidate.priceValue * 2.5),
    })
  }

  return { analyses, tats, addons, deals }
}

/**
 * Writes the pricing document, preserving `createdAt`/`createdByUserID` when
 * one already exists. `setDoc` rather than `updateDoc` so a customer that has
 * never had pricing gets a complete document instead of throwing.
 */
async function writePricingDoc({
  collectionName,
  ownerIDField,
  entityID,
  labAccountID,
  userID,
  pricingSegmentID,
  currencyCode,
  overrides,
}: {
  collectionName: string
  ownerIDField: string
  entityID: string
  labAccountID: string
  userID: string
  pricingSegmentID: string
  currencyCode: string
  overrides: IOverrideSet
}): Promise<void> {
  const ref = doc(db, 'Apps', '-lab-management-', collectionName, entityID)
  const existing = await getDoc(ref)
  const existingData = existing.data()
  const now = Date.now()

  await setDoc(ref, {
    ...(existingData ?? {}),
    id: entityID,
    labAccountID,
    [ownerIDField]: entityID,
    pricingSegmentID,
    currencyCode,
    analyses: overrides.analyses,
    tats: overrides.tats,
    addons: overrides.addons,
    deals: overrides.deals,
    createdByUserID: (existingData?.createdByUserID as string) ?? userID,
    timestamps: {
      createdAt: (existingData?.timestamps?.createdAt as number) ?? now,
      updatedAt: now,
    },
  })
}

export async function runSeedCustomerPricing(
  accountID: string,
  userID: string
): Promise<ISeedCustomerPricingResult> {
  const allAnalyses = useAnalysisStore().allAnalyses
  const allTATs = useTurnaroundTimeStore().allTATs
  const currentLocale = useAccountStore().labAccount?.defaultLanguageCode ?? 'da'

  // The segment to inherit from: first active one alphabetically that actually
  // has prices configured. Deterministic, so re-running seeds the same thing.
  const segment = [...usePricingSegmentStore().allPricingSegments]
    .filter(
      (x) =>
        x.activeState === 'active' && x.analyses.length > 0 && x.tats.length > 0
    )
    .sort((a, b) =>
      a.getName(currentLocale).localeCompare(b.getName(currentLocale))
    )[0]

  if (!segment) {
    throw new Error(
      '[runSeedCustomerPricing] No active pricing segment with prices — seed the catalog first'
    )
  }

  const segmentAnalyses = [...segment.analyses].sort((a, b) =>
    a.analysisID.localeCompare(b.analysisID)
  )
  const segmentTats = [...segment.tats].sort(
    (a, b) =>
      (allTATs.find((x) => x.id === a.tatID)?.durationMilliseconds ?? 0) -
      (allTATs.find((x) => x.id === b.tatID)?.durationMilliseconds ?? 0)
  )
  // An add-on the segment leaves unpriced has nothing to override, so it is
  // dropped rather than seeded as an override of nothing.
  const segmentAddons = [...(segment.addons ?? [])]
    .flatMap((addon) =>
      addon.priceValue === null
        ? []
        : [{ addonID: addon.addonID, priceValue: addon.priceValue }]
    )
    .sort((a, b) => a.addonID.localeCompare(b.addonID))

  // Scoped lines must name analyses the TAT can actually serve — an
  // incompatible one renders as a stale-price warning, which is the confusing
  // state this seed exists to avoid producing.
  const segmentAnalysisIDs = new Set(segmentAnalyses.map((x) => x.analysisID))
  const compatibleAnalysisIDsByTatID = new Map<string, string[]>(
    segmentTats.map((entry) => {
      const tat = allTATs.find((x) => x.id === entry.tatID)
      const compatible = tat
        ? (tat.getCompatibleAnalyses(allAnalyses, allTATs) as Analysis[])
        : []
      return [
        entry.tatID,
        compatible
          .map((x) => x.id)
          .filter((id) => segmentAnalysisIDs.has(id))
          .sort((a, b) => a.localeCompare(b)),
      ]
    })
  )

  const segmentDeals = [...segment.deals].sort((a, b) =>
    a.analysisID.localeCompare(b.analysisID)
  )

  const overrides = buildOverrideSet({
    segmentAnalyses,
    segmentTats,
    segmentAddons,
    segmentDeals,
    compatibleAnalysisIDsByTatID,
  })

  const result: ISeedCustomerPricingResult = {
    segmentName: segment.getName(currentLocale),
    seeded: [],
    skipped: [],
  }

  // CustomerAccount and InsuranceCustomerAccount carry a plain `name: string`, not an ITextObj —
  // unlike PricingSegment, which is why this doesn't take a locale.
  const byName = (a: { name: string }, b: { name: string }) =>
    a.name.localeCompare(b.name)

  // Business customer account
  const customerAccount = [...useCustomerAccountStore().allCustomerAccounts]
    .filter((x) => x.activeState === 'active')
    .sort(byName)[0]
  if (customerAccount) {
    await writePricingDoc({
      collectionName: 'CustomerAccountPrices',
      ownerIDField: 'customerAccountID',
      entityID: customerAccount.id,
      labAccountID: accountID,
      userID,
      pricingSegmentID: segment.id,
      currencyCode: segment.currencyCode,
      overrides,
    })
    result.seeded.push({
      label: 'Erhvervskunde',
      name: customerAccount.name,
    })
  } else {
    result.skipped.push({
      label: 'Erhvervskunde',
      reason: 'ingen aktive erhvervskunder',
    })
  }

  // Insurance customer account
  const insuranceCustomerAccount = [...useInsuranceCustomerAccountStore().allInsuranceCustomerAccounts]
    .filter((x) => x.activeState === 'active')
    .sort(byName)[0]
  if (insuranceCustomerAccount) {
    await writePricingDoc({
      collectionName: 'InsuranceCustomerAccountPrices',
      ownerIDField: 'insuranceCustomerAccountID',
      entityID: insuranceCustomerAccount.id,
      labAccountID: accountID,
      userID,
      pricingSegmentID: segment.id,
      currencyCode: segment.currencyCode,
      overrides,
    })
    result.seeded.push({
      label: 'Forsikringskunde',
      name: insuranceCustomerAccount.name,
    })
  } else {
    result.skipped.push({ label: 'Forsikringskunde', reason: 'ingen aktive forsikringskunder' })
  }

  // Private customer
  const privateUser = [...useCustomerUserStore().customerUsersPrivate]
    .filter((x) => x.activeState === 'active')
    .sort((a, b) => a.fullName.localeCompare(b.fullName))[0]
  if (privateUser) {
    await writePricingDoc({
      collectionName: 'CustomerPrivatePrices',
      ownerIDField: 'privateUserID',
      entityID: privateUser.id,
      labAccountID: accountID,
      userID,
      pricingSegmentID: segment.id,
      currencyCode: segment.currencyCode,
      overrides,
    })
    result.seeded.push({ label: 'Privatkunde', name: privateUser.fullName })
  } else {
    result.skipped.push({
      label: 'Privatkunde',
      reason: 'ingen aktive privatkunder',
    })
  }

  return result
}
